import { useCallback } from 'react';
import { useNavigate, type NavigateFunction, type NavigateOptions, type To } from 'react-router-dom';

/**
 * `useNavigate` con View Transitions habilitadas por default. **Es el hook de
 * navegación canónico de la app**: usalo en lugar de `useNavigate` en TODO
 * call-site (chrome de navegación, CTAs y redirects programáticos), para que el
 * cambio de sección sea siempre un cross-fade suave y no un corte seco.
 *
 * react-router v7 envuelve el cambio de ruta en `document.startViewTransition`
 * cuando se le pasa `viewTransition: true`, lo que dispara el cross-fade global
 * definido en `styles/global.css` (`::view-transition-old/new(root)`). La
 * animación real —y su supresión bajo `prefers-reduced-motion`— vive 100% en CSS.
 *
 * Drop-in de `useNavigate`: respeta la misma firma, incluido el overload
 * `navigate(delta)` (back/forward), que NO animamos vía VT (un back animado
 * cruza mal con el snapshot del UA). Para saltar la animación en un caso
 * puntual, pasá `{ viewTransition: false }` en las opciones.
 *
 * Degradación: en navegadores sin la View Transitions API, react-router navega
 * normalmente (sin animación). No hay fallback que mantener acá.
 */
export function useViewNavigate(): NavigateFunction {
  const navigate = useNavigate();
  return useCallback<NavigateFunction>(
    (to: To | number, options?: NavigateOptions) => {
      // Overload histórico `navigate(-1)` / `navigate(1)`: back/forward sin VT.
      if (typeof to === 'number') {
        void navigate(to);
        return;
      }
      void navigate(to, { viewTransition: true, ...options });
    },
    [navigate],
  );
}
